import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Product as IProduct } from '../models/interfaces';
import ProductTooltip from './ProductTooltip';
import { MODAL_STORE } from '../constants/constants';
import * as placeholderImage from '../assets/images/product-placeholder.png';

const intervalTime: number = 50;

interface ProductProps {
  product: IProduct;
  addProduct: (product: IProduct, amount: number) => void;
  showItemNo: boolean;
  showImage: boolean;
  totalWeight: string;
  showWeightInput: boolean;
}

interface ProductState {
  amount: number | null;
  tooltip: boolean;
  tooltipTimeout: any; // tslint:disable-line
  timeoutInterval: any; // tslint:disable-line
  holdTime: number;
}

@inject(
  MODAL_STORE
)
@observer
class Product extends React.Component<ProductProps, ProductState> {

  constructor(props: ProductProps) {
    super(props);
    this.state = {
      amount: 1,
      tooltip: false,
      tooltipTimeout: null,
      timeoutInterval: null,
      holdTime: 0
    };
  }

  componentDidUpdate(prevProps: ProductProps) {
    // if product has grossweight and total weight is passed as prop, then calculate amount
    if (this.props.totalWeight !== prevProps.totalWeight) {
      if (this.props.totalWeight &&
        this.props.product.GrossWeight &&
        (this.props.product.GrossWeight !== '0')) {
          this.calculateAmount(this.props.totalWeight, this.props.product.GrossWeight);
        } else {
          this.setState({ amount: 1 });
        }
    }
  }

  calculateAmount(totalWeight: string, productWeight: string) {
    const parsedProductWeight = parseFloat(productWeight);
    const amount = Math.floor(parseInt(totalWeight, 10) / parsedProductWeight);
    this.setState({ amount });
  }

  onAmountType(amountString: string) {
    const amount =  parseInt(amountString, 10);
    if (amount) {
      this.setState({ amount });
    } else {
      this.setState({ amount: null });
    }
  }

  onChangeAmount(amount: number) {
    if (this.state.amount) {
      const newAmount = this.state.amount + amount;
      if (newAmount > 0) {
        this.setState({ amount: newAmount });
      }
    } else {
      this.setState({ amount: amount });
    }
  }

  addProduct(product: IProduct, amount: number) {
    // @TODO Custom handler when adding product to cart.
    // must show indication that product was added.
    if (this.state.amount) {
      this.props.addProduct(product, amount);

      if (this.state.tooltipTimeout) {
        clearTimeout(this.state.tooltipTimeout);
      }

      this.setState({ tooltip: false, tooltipTimeout: null });
      setTimeout(
        () => { this.showTooltip(); },
        10
      );
    }
  }

  showTooltip() {
    this.setState({
      tooltip: true,
      tooltipTimeout: setTimeout(
        () => { this.setState({ tooltip: false }); },
        500)
    });
  }

  onChangeAmoutHold(amount: number, action: string) {
    if (!this.state.timeoutInterval && action === 'start') {
      this.setState({
        timeoutInterval: setInterval(
          () => {
            this.setState({ holdTime: this.state.holdTime + intervalTime });
            if (this.state.holdTime >= 200) {
              this.onChangeAmount(amount);
            }
          },
          intervalTime)
      });
    } else {
      clearInterval(this.state.timeoutInterval);
      this.setState({
        timeoutInterval: null,
        holdTime: 0
      });
    }
  }

  imageClasses(product: IProduct) {
    const baseClass = 'product__image';

    if (!product.image) {
      return baseClass;
    }

    if (!this.props.showImage) {
      return baseClass + ' hide';
    }

    return baseClass + ' lazy';
  }

  grossWeight() {
    const { product } = this.props;

    if (!this.props.showWeightInput) {
      return null;
    }

    if (product.GrossWeight && product.GrossWeight !== '0') {
      const weight = product.GrossWeight;
      return (
        <div className="product__gross-weight">
          <input
            type="number"
            min="1"
            onChange={(e) => this.calculateAmount(e.target.value, weight)}
            placeholder="Paino (g)"
          />
          <span> / </span>
          <span>{weight}g á</span>
        </div>
      );
    }

    return null;
  }

  onClickImage() {
    const { product } = this.props;

    if (!product.image) {
      return;
    }

    this.props[MODAL_STORE].clearStore();
    this.props[MODAL_STORE].onlyCloseButton = true;
    this.props[MODAL_STORE].setChildren(
      <img
        className="product__image large"
        src={product.image ? product.image : placeholderImage}
        alt="product"
      />
    );
    this.props[MODAL_STORE].setSize('large');
    this.props[MODAL_STORE].openModal();
  }

  render() {
    const {
      Description,
      UnitOfMeasure,
      ItemNo,
      Inventory,
      Reserved,
      ItemCategoryCode } = this.props.product;
    const {
      showItemNo,
      product } = this.props;

    const productReserved = () => {
      if (!Reserved || Reserved === '0') {
        return null;
      }

      return (
        <span className="product__reserved">varaukset -{Reserved}</span>
      );
    };

    return (
      <div className="product">
        <div className={this.imageClasses(product)} onClick={() => this.onClickImage()}>
          <img
            src={placeholderImage}
            data-src={product.image_thumb ? product.image_thumb : placeholderImage}
            alt="product image"
          />
        </div>
        <div className="product__data">
          <div className="product__title">{Description}</div>
          {showItemNo ? <div className="product__id">{ItemNo}</div> : null}
        </div>
        <div className="product__actions">
          <div className="product__actions-container">
            {this.grossWeight()}
            <div className="product__amount">
              <button
                className="decrease"
                onClick={() => this.onChangeAmount(-1)}
                onMouseDown={() => this.onChangeAmoutHold(-1, 'start')}
                onMouseUp={() => this.onChangeAmoutHold(-1, 'stop')}
                onMouseLeave={() => this.onChangeAmoutHold(-1, 'stop')}
                onTouchStart={() => this.onChangeAmoutHold(-1, 'start')}
                onTouchEnd={() => this.onChangeAmoutHold(-1, 'stop')}
                onTouchCancel={() => this.onChangeAmoutHold(-1, 'stop')}
              >-
              </button>
              <div className="amount">
                <input
                  type="text"
                  min="1"
                  onChange={(e) => this.onAmountType(e.target.value)}
                  value={this.state.amount || ''}
                />
                <span className="measure">{UnitOfMeasure}</span>
              </div>
              <button
                className="increase"
                onClick={() => this.onChangeAmount(1)}
                onMouseDown={() => this.onChangeAmoutHold(1, 'start')}
                onMouseUp={() => this.onChangeAmoutHold(1, 'stop')}
                onMouseLeave={() => this.onChangeAmoutHold(1, 'stop')}
                onTouchStart={() => this.onChangeAmoutHold(1, 'start')}
                onTouchEnd={() => this.onChangeAmoutHold(1, 'stop')}
                onTouchCancel={() => this.onChangeAmoutHold(1, 'stop')}
              >+
              </button>
            </div>
            <div className="product__add">
              <div
                className="button button--small"
                onClick={() => this.addProduct(product, this.state.amount || 1)}
              >Lisää »
              </div>
              {
                this.state.tooltip ? (
                  <ProductTooltip amount={this.state.amount || 1} />
                ) : null
              }
            </div>
          </div>
          <div className="product__inventory">
            Varastossa {Inventory}
            {productReserved()}
          </div>
        </div>
      </div>
    );
  }
}

export default Product;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Product.tsx